import { FC, MouseEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import {
  MButton,
  MTextBlockSimple,
  MTextBlockSimpleAlignment,
  MButtonIconPlacement,
  ABodySize,
} from '@marriott/mi-web-ui-kit';
import type { MButtonType, MTextBlockSimpleProps } from '@marriott/mi-web-ui-kit';

import { StyledCardComparisonHeader } from './CardComparisonHeader.styles';
import { CardComparisonHeaderButtonProps, CardComparisonHeaderProps } from './CardComparisonHeader.types';
import { SESSION_CONSTANTS } from '../../modules/constants';
import { useStore } from '../../modules/store/cobrandStore';
import { usePageContext } from '../../context/PageContext';
import {
  getStoredCreditCardEndpoint,
  isFromFOCComparisonEntry,
  persistComparisonCardSelection,
} from '../../modules/utils';

export const CardComparisonHeaderConfig = {
  emptyLabel: 'Card Comparison Header',
  isEmpty: () => true,
  resourceType: `/components/content/cardcomparisonheader`,
};

type HeaderButtonClickHandler = (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
type FromFOCValue = boolean | string | string[] | undefined;

const getSessionStorageHasSelectedCards = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const stored = sessionStorage?.getItem(SESSION_CONSTANTS.COBRAND_DATA);
    if (!stored) return false;
    const parsed = JSON.parse(stored);
    return (
      typeof parsed === 'object' &&
      parsed !== null &&
      Array.isArray(parsed.selectedCards) &&
      parsed.selectedCards.some((c: { checked?: boolean }) => c.checked === true)
    );
  } catch {
    return false;
  }
};

export const CardComparisonHeaderComp: FC<CardComparisonHeaderProps> = memo((props: CardComparisonHeaderProps) => {
  const { componentId, styleclass } = props;
  const pageContext = usePageContext();
  const fromFOCContext = pageContext?.fromFOC as FromFOCValue;
  const { cobrandData, setCobrandData } = useStore(store => store);

  // All three initial values are kept falsy/undefined so the server and client
  // render identical HTML on the first pass — preventing the hydration mismatch.
  const [isFromFOC, setIsFromFOC] = useState(false);
  const [hasSelectedCards, setHasSelectedCards] = useState(false);
  const [sessionStorageEndpoint, setSessionStorageEndpoint] = useState<string | undefined>();

  const textBlockProps = useMemo((): MTextBlockSimpleProps | undefined => {
    if (props.mTextBlockSimple) return props.mTextBlockSimple;
    if (props.header || props.description) {
      const stripHtml = (s?: string) => (s ? s.replace(/<[^>]*>/g, '').trim() : s);
      return {
        headerText: stripHtml(props.header),
        bodyText: stripHtml(props.description),
        bodySize: ABodySize.M,
      } as MTextBlockSimpleProps;
    }
    return undefined;
  }, [props]);

  const primaryButton = useMemo(() => props.mButton ?? props.primaryCTA, [props]);
  const secondaryButton = useMemo(() => props.mButton2 ?? props.secondaryCTA, [props]);

  // All three state values are updated inside ONE effect so React batches the
  // three setState calls into a single re-render.  The previous code had two
  // separate effects with different dependency arrays, which caused an
  // intermediate render where one value was already updated but the other was
  // still stale — leaving the component in a state where neither button
  // condition evaluated to true and nothing was shown.
  useEffect(() => {
    const storeCards = cobrandData?.selectedCards ?? [];
    const nextHasSelected =
      storeCards.length > 0 ? storeCards.some(card => card.checked) : getSessionStorageHasSelectedCards();

    const nextIsFromFOC =
      isFromFOCComparisonEntry(fromFOCContext) ||
      isFromFOCComparisonEntry(new URLSearchParams(window.location.search).get('fromFOC') ?? undefined);

    setIsFromFOC(nextIsFromFOC);
    setHasSelectedCards(nextHasSelected);
    setSessionStorageEndpoint(getStoredCreditCardEndpoint());
  }, [cobrandData?.selectedCards, fromFOCContext]);

  const handleComparisonHeaderExit = useCallback(() => {
    setCobrandData({
      ...cobrandData,
      selectedCards: [],
    });

    if (typeof window !== 'undefined') {
      sessionStorage?.removeItem(SESSION_CONSTANTS.COBRAND_DATA);
      sessionStorage?.removeItem(SESSION_CONSTANTS.CREDIT_CARD_ENDPOINT);
    }
  }, [cobrandData, setCobrandData]);

  const handlePrimaryCTAClick = useCallback(() => {
    const selectedCardIds = (cobrandData?.selectedCards ?? [])
      .filter(card => card?.checked)
      .map(card => card.cardname)
      .filter((id): id is string => Boolean(id));

    if (selectedCardIds.length) {
      persistComparisonCardSelection(selectedCardIds, cobrandData as Record<string, unknown>);
    }
  }, [cobrandData]);

  const makeButtonProps = (
    button: CardComparisonHeaderButtonProps,
    sessionStorageEndpoint?: string,
    isPrimaryCTA?: boolean,
    onClick?: HeaderButtonClickHandler
  ) => {
    const openInANewTab = button.openInANewTab ?? button.openInNewTab ?? button.openInaNewTab;
    const linkType = openInANewTab === true || openInANewTab === 'true' ? 'external' : 'internal';
    let resolvedLinkUrl: string | undefined;
    const wantsSessionEndpoint =
      button.useSessionStorageEndpoint === true || button.useSessionStorageEndpoint === 'true';
    if (wantsSessionEndpoint && sessionStorageEndpoint) {
      resolvedLinkUrl = sessionStorageEndpoint;
    } else {
      resolvedLinkUrl = button.ctaLink ?? button.href ?? button.linkUrl;
    }

    const normalizedTrackingProperties = button.trackingProperties ?? {
      additionalTrackingVariables: button.additionalTrackingVariables ?? '',
      trackingDescription: button.trackingDescription ?? '',
      trackingContentPosition: button.trackingContentPosition ?? '',
      atCCeVar48: button.atCCeVar48,
      clickTrack: button.clickTrack === true || button.clickTrack === 'true' ? true : undefined,
      impressionTrack: button.impressionTrack === true || button.impressionTrack === 'true' ? true : undefined,
      merchandisingCategory: button.merchandisingCategory,
      impressionCount: button.impressionCount === true || button.impressionCount === 'true' ? true : undefined,
      trackingTag: button.trackingTag,
      trackingOfferType: button.trackingOfferType,
      faicUrl: button.faicUrl,
      faicMCD: button.faicMCD,
    };

    return {
      buttonText: button.buttonText ?? button.linkText,
      ctaLink: resolvedLinkUrl,
      openInANewTab,
      trackingProperties: normalizedTrackingProperties,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      size: (button.size ?? button.size) as any,
      iconPlacement: isPrimaryCTA ? MButtonIconPlacement.Leading : MButtonIconPlacement.Trailing,
      iconClass: isPrimaryCTA ? 'icon-back-arrow' : 'icon-arrow-right',
      custom_click_track_value:
        button.custom_click_track_value ??
        `${button.trackingContentPosition ?? ''}|${button.trackingDescription ?? ''}|${linkType}|${
          button.additionalTrackingVariables ?? ''
        }`,
      onClick: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        (button.onClick as HeaderButtonClickHandler | undefined)?.(event);
        if (!event.defaultPrevented) {
          onClick?.(event);
        }
      },
      buttonType: (button.buttonType ?? button.variant ?? button.type ?? 'Secondary') as MButtonType,
    };
  };

  return (
    <StyledCardComparisonHeader
      className={clsx('comparison-page-header my-md-5 py-lg-3 my-3', styleclass)}
      data-component-name="o-cobrand-CardComparisonHeader"
      data-testid="cobrand-CardComparisonHeader"
      id={componentId}
    >
      {isFromFOC &&
        hasSelectedCards &&
        primaryButton &&
        (primaryButton.buttonText || primaryButton.linkText || primaryButton.children) && (
          <div className="comparison-page-header__action comparison-page-header__action--top">
            <MButton {...makeButtonProps(primaryButton, sessionStorageEndpoint, true, handlePrimaryCTAClick)} />
          </div>
        )}
      {Boolean(textBlockProps?.eyebrowText || textBlockProps?.headerText || textBlockProps?.bodyText) && (
        <div className="comparison-page-header__text mt-lg-5 mt-md-3 mt-0 mx-3 mx-md-0">
          <MTextBlockSimple {...textBlockProps} alignment={MTextBlockSimpleAlignment.Center} />
        </div>
      )}
      {!isFromFOC &&
        hasSelectedCards &&
        secondaryButton &&
        (secondaryButton.buttonText || secondaryButton.linkText || secondaryButton.children) && (
          <div className="comparison-page-header__action comparison-page-header__action--bottom">
            <MButton {...makeButtonProps(secondaryButton, undefined, false, handleComparisonHeaderExit)} />
          </div>
        )}
    </StyledCardComparisonHeader>
  );
});

CardComparisonHeaderComp.displayName = 'CardComparisonHeaderComp';

export const CardComparisonHeader = (props: CardComparisonHeaderProps) => (
  <EditableComponent config={CardComparisonHeaderConfig} {...props}>
    <CardComparisonHeaderComp {...props} />
  </EditableComponent>
);
